import { useCallback, useEffect, useRef, useState } from 'react';
import type { DragEvent } from 'react';

import { hasDraggedFiles, pickDroppedFile } from '../lib/file-drop';

// Web-only. Returns the props a drop target needs plus an `isDraggingOver`
// flag for the highlight. Also suppresses the window-level default, so a file
// dropped just outside the box does not navigate the tab away from the app
// (issue #254).

type Options = {
  accept?: string;
  disabled?: boolean;
  onFile: (file: File) => void;
  onRejected?: () => void;
};

export function useFileDropZone({
  accept,
  disabled = false,
  onFile,
  onRejected,
}: Options) {
  const [isDraggingOver, setIsDraggingOver] = useState(false);
  // Drag events fire for every child element; a counter keeps the highlight
  // stable instead of flickering as the pointer crosses inner nodes.
  const depth = useRef(0);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const swallow = (event: Event) => {
      const dragEvent = event as unknown as {
        dataTransfer?: { types?: readonly string[] };
      };
      if (hasDraggedFiles(dragEvent.dataTransfer?.types)) {
        event.preventDefault();
      }
    };

    window.addEventListener('dragover', swallow);
    window.addEventListener('drop', swallow);
    return () => {
      window.removeEventListener('dragover', swallow);
      window.removeEventListener('drop', swallow);
    };
  }, []);

  const reset = useCallback(() => {
    depth.current = 0;
    setIsDraggingOver(false);
  }, []);

  const onDragEnter = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (disabled || !hasDraggedFiles(event.dataTransfer?.types)) return;
      event.preventDefault();
      depth.current += 1;
      setIsDraggingOver(true);
    },
    [disabled],
  );

  const onDragOver = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (disabled || !hasDraggedFiles(event.dataTransfer?.types)) return;
      // Without this the browser keeps its default handler and never fires drop.
      event.preventDefault();
      if (event.dataTransfer) event.dataTransfer.dropEffect = 'copy';
    },
    [disabled],
  );

  const onDragLeave = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (disabled) return;
      event.preventDefault();
      depth.current = Math.max(depth.current - 1, 0);
      if (depth.current === 0) setIsDraggingOver(false);
    },
    [disabled],
  );

  const onDrop = useCallback(
    (event: DragEvent<HTMLElement>) => {
      if (disabled) return;
      event.preventDefault();
      reset();

      const dropped = Array.from(event.dataTransfer?.files ?? []);
      if (dropped.length === 0) return;

      const file = pickDroppedFile(dropped, accept);
      if (!file) {
        onRejected?.();
        return;
      }
      onFile(file);
    },
    [accept, disabled, onFile, onRejected, reset],
  );

  return {
    isDraggingOver,
    dropZoneProps: { onDragEnter, onDragOver, onDragLeave, onDrop },
  };
}
